import React, { useState, useEffect, useCallback } from 'react';
import { Image, X, Play, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchPublicVideos } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import VideoReaction from '../components/VideoReaction';
import UnifiedHeader from '../components/UnifiedHeader';
import './PublicGallery.css';

export default function PublicGallery() {
  const { language } = useLanguage();
  const t = translations[language];

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('recent');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showQuestionImage, setShowQuestionImage] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [playingVideoId, setPlayingVideoId] = useState(null);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPublicVideos({
        page,
        limit: 12,
        sort: sortBy,
        subject: filterSubject,
        language: filterLanguage,
        search: searchQuery
      });
      setVideos(data.videos || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalCount(data.pagination?.totalCount || 0);
    } catch (error) {
      console.error('Error loading videos:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, filterSubject, filterLanguage, searchQuery]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadVideos();
  };

  const handleThumbnailClick = (videoId) => {
    setPlayingVideoId(videoId);
  };

  const handleQuestionImageClick = (e, imageUrl) => {
    e.stopPropagation();
    setSelectedImageUrl(imageUrl);
    setShowQuestionImage(true);
  };

  const handleCloseQuestionImage = () => {
    setShowQuestionImage(false);
    setSelectedImageUrl('');
  };

  const getLanguageFlag = (lang) => {
    switch (lang) {
      case 'fi': return '🇫🇮';
      case 'en': return '🇬🇧';
      case 'sv': return '🇸🇪';
      default: return '🌐';
    }
  };

  const getSubjectEmoji = (subject) => {
    switch (subject?.toLowerCase()) {
      case 'chemistry': return '🧪';
      case 'physics': return '⚛️';
      case 'mathematics': return '📐';
      default: return '📚';
    }
  };

  const getSubjectName = (subject) => {
    if (!subject) return t.publicGallery?.general || 'General';
    const subjectMap = {
      'chemistry': t.chemistry || 'Chemistry',
      'physics': t.physics || 'Physics',
      'mathematics': t.mathematics || 'Mathematics',
    };
    return subjectMap[subject.toLowerCase()] || subject;
  };

  return (
    <div className="public-gallery">
      <UnifiedHeader />
      <div className="gallery-container">
        {/* Header */}
        <div className="gallery-header">
          <h1 className="gallery-title">
            {t.publicGallery?.title || 'Public Gallery'}
          </h1>
          <p className="gallery-subtitle">
            {t.publicGallery?.subtitle || 'Explore educational videos shared by the community'}
          </p>
        </div>

        {/* Search Section */}
        <div className="search-section">
          <form className="search-form" onSubmit={handleSearch}>
            <div className="search-input-wrapper">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                className="search-input"
                placeholder={t.publicGallery?.searchPlaceholder || 'Search videos...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button type="submit" className="search-button">
              {t.publicGallery?.search || 'Search'}
            </button>
          </form>
        </div>

        {/* Gallery Controls */}
        <div className="gallery-controls">
          <div className="gallery-stats">
            <span className="total-count">
              {totalCount} {totalCount === 1 ? (t.publicGallery?.video || 'video') : (t.publicGallery?.videos || 'videos')}
            </span>
          </div>

          <div className="gallery-filters">
            {/* Subject Filter */}
            <select
              className="filter-select"
              value={filterSubject}
              onChange={(e) => { setFilterSubject(e.target.value); setPage(1); }}
            >
              <option value="all">{t.publicGallery?.allSubjects || 'All Subjects'}</option>
              <option value="mathematics">{t.mathematics || 'Mathematics'}</option>
              <option value="physics">{t.physics || 'Physics'}</option>
              <option value="chemistry">{t.chemistry || 'Chemistry'}</option>
            </select>

            {/* Language Filter */}
            <select
              className="filter-select"
              value={filterLanguage}
              onChange={(e) => { setFilterLanguage(e.target.value); setPage(1); }}
            >
              <option value="all">{t.publicGallery?.allLanguages || 'All Languages'}</option>
              <option value="fi">🇫🇮 Suomi</option>
              <option value="en">🇬🇧 English</option>
              <option value="sv">🇸🇪 Svenska</option>
            </select>

            {/* Sort Filter */}
            <select
              className="filter-select"
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            >
              <option value="recent">{t.publicGallery?.mostRecent || 'Most Recent'}</option>
              <option value="popular">{t.publicGallery?.mostPopular || 'Most Popular'}</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">{t.publicGallery?.loading || 'Loading videos...'}</p>
          </div>
        )}

        {/* Videos Grid */}
        {!loading && videos.length > 0 && (
          <>
            <div className="videos-grid">
              {videos.map((video) => (
                <div key={video.id} className="public-video-card">
                  {/* Thumbnail Wrapper */}
                  <div className="video-thumbnail-wrapper">
                    {playingVideoId === video.id ? (
                      <video
                        className="inline-video-player"
                        controls
                        controlsList="nodownload"
                        autoPlay
                        onEnded={() => setPlayingVideoId(null)}
                      >
                        <source src={video.video_url} type="video/mp4" />
                        {t.videoCard?.noVideoSupport || 'Your browser does not support the video tag.'}
                      </video>
                    ) : (
                      <>
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt={video.scene_name} />
                        ) : (
                          <div className="thumbnail-placeholder">🎬</div>
                        )}
                        <div 
                          className="video-overlay"
                          onClick={() => handleThumbnailClick(video.id)}
                        >
                          <button className="play-button">
                            <Play size={24} fill="currentColor" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Reaction bar — directly under video */}
                  <VideoReaction videoId={video.id} showCounts compact />

                  {/* Video Info */}
                  <div className="video-info">
                    <h3 className="video-title">{video.scene_name}</h3>

                    {/* Meta Badges */}
                    <div className="video-meta-row">
                      <span className="meta-badge subject">
                        {getSubjectEmoji(video.subject)} {getSubjectName(video.subject)}
                      </span>
                      <span className="meta-badge language">
                        {getLanguageFlag(video.language)}
                      </span>
                      <span className="meta-badge date">
                        {new Date(video.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Actions */}
                    {video.question_image_url && (
                      <div className="video-actions">
                        <button
                          className="action-btn question-image"
                          onClick={(e) => handleQuestionImageClick(e, video.question_image_url)}
                          title={t.publicGallery?.questionImage || 'Question Image'}
                        >
                          <Image size={20} strokeWidth={2} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-button"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft size={20} />
                  {t.publicGallery?.previous || 'Previous'}
                </button>
                <div className="pagination-info">
                  <span className="page-numbers">
                    {t.publicGallery?.page || 'Page'} {page} {t.publicGallery?.of || 'of'} {totalPages}
                  </span>
                </div>
                <button
                  className="pagination-button"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  {t.publicGallery?.next || 'Next'}
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </>
        )}

        {/* No Videos State */}
        {!loading && videos.length === 0 && (
          <div className="no-videos-container">
            <p className="no-videos-text">
              {t.publicGallery?.noVideos || 'No videos found'}
            </p>
          </div>
        )}
      </div>

      {/* Question Image Modal */}
      {showQuestionImage && selectedImageUrl && (
        <div className="question-image-modal-overlay" onClick={handleCloseQuestionImage}>
          <div className="question-image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="question-image-modal-header">
              <h3 className="question-image-modal-title">{t.publicGallery?.questionImage || 'Question Image'}</h3>
              <button
                className="question-image-modal-close"
                onClick={handleCloseQuestionImage}
              >
                <X />
              </button>
            </div>
            <div className="question-image-modal-body">
              <img src={selectedImageUrl} alt="Question" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}